import { sortBy } from 'lodash';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { useSelectedLanguageContext } from '~/components/SelectedLanguage';
import {
  CollaborationWorkList,
  CollaborationWorkListTemplate,
  useGetCollaborationWorkListTemplates,
} from '~/data/collaboration';
import { EnumListKey, useAllEnums } from '~/data/enums';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { useHasChanged } from '~/hooks/useHasChanged';
import { useYupValidationResolver } from '~/hooks/useYupValidationResolver';
import { WorklistTaskFormData } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/EditWorklistTaskDialog/data';
import { useWorklistTaskValidation } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/EditWorklistTaskDialog/useWorklistTaskValidation';
import { useResponsibleHRSelect } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';

import {
  convertTaskListToEmployeeTasks,
  convertTemplateTaskToTaskFormData,
  createEmployeeTemplateTasks,
  createNewTaskFormData,
} from '../data';

export type CollabTemplateFormType = {
  creatorId: number;
  templateId?: number;
  type?: string;
  targetEmployeeId: number;
  startDate?: Date;
  dueDate?: Date;
  language: string;
  listTasks: WorklistTaskFormData[];
};

type Props = {
  workListOptions: CollaborationWorkList[];
  onClose: () => void;
  onTaskListCreate: () => void;
};

export const useAddWorklistDialog = (props: Props) => {
  const { workListOptions, onClose: onDialogClose, onTaskListCreate } = props;

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [newTaskData, setNewTaskData] = useState<
    WorklistTaskFormData | undefined
  >(undefined);

  const loggedInUserId = useLoggedInEmployeeId();
  const {
    selectedEmployeeDetails: { employeeData },
    selectedEmployeeId,
  } = useSelectedEmployeeId();

  const { language } = useSelectedLanguageContext();

  const { getEnums } = useAllEnums();
  const { taskTypeEnums } = useMemo(() => {
    return {
      taskTypeEnums: getEnums(EnumListKey.COLLAB_TASK_TYPE),
    };
  }, [getEnums]);
  const getTaskTypeName = useCallback(
    (typeCode: string | undefined): string | undefined =>
      taskTypeEnums.find((e) => e.code === typeCode)?.name,
    [taskTypeEnums],
  );

  const [submitting, setSubmitting] = useState<boolean>(false);

  const { defaultResponsibleHrId } = useResponsibleHRSelect(selectedEmployeeId);

  const initialFormData: CollabTemplateFormType = useMemo(() => {
    return {
      creatorId: loggedInUserId,
      targetEmployeeId: selectedEmployeeId,
      language: language ?? 'en',
      listTasks: [],
      startDate: moment().toDate(),
      dueDate: moment().add(30, 'days').toDate(),
    };
  }, [loggedInUserId, selectedEmployeeId, language]);

  const { validationSchema: taskValidationSchema } =
    useWorklistTaskValidation();

  const errorMessages = useMemo(() => {
    return {
      requiredField: t('fieldIsRequired'),
      worklistHasNoTasks: t('errorWorklistHasNoTasks'),
    };
  }, [t]);
  const validationSchema = useMemo(() => {
    const { requiredField, worklistHasNoTasks } = errorMessages;

    return yup
      .object()
      .shape<CollabTemplateFormType>({
        creatorId: yup.number().integer().required(requiredField),
        templateId: yup.number().integer().required(requiredField),
        type: yup.string().required(requiredField),
        targetEmployeeId: yup.number().integer().required(requiredField),
        startDate: yup.date().required(requiredField),
        dueDate: yup.date().required(requiredField),
        language: yup.string().required(requiredField),
        listTasks: yup
          .array()
          .of<WorklistTaskFormData>(taskValidationSchema.required())
          .required(),
      })
      .test('taskListSize', worklistHasNoTasks, (value, context) => {
        // Hacky workaround to test if array is empty
        const path = !!context.path ? `${context.path}.listTasks` : 'listTasks';

        return value?.listTasks.length === 0
          ? context.createError({
              message: worklistHasNoTasks,
              path: path,
            })
          : true;
      });
  }, [taskValidationSchema, errorMessages]);

  const resolver = useYupValidationResolver(
    validationSchema,
    'react-hook-form',
  );

  const form = useForm<CollabTemplateFormType>({
    resolver,
    defaultValues: initialFormData,
  });
  const { setValue, watch, reset, handleSubmit, getValues } = form;

  const {
    templateId: selectedTemplateId,
    startDate: templateStartDate,
    dueDate: templateDueDate,
  } = watch();

  const { data: workListTemplates } = useGetCollaborationWorkListTemplates({
    workListIds: workListOptions.map((wl) => wl.fCollaborationTemplateId),
    language,
  });

  const getTemplateById = useCallback(
    (templateId: number): CollaborationWorkListTemplate | undefined => {
      return workListTemplates?.find(
        (tmp) => templateId === tmp.fCollaborationTemplateId,
      );
    },
    [workListTemplates],
  );

  const handleSyncTemplateType = useCallback(
    (templateId: number) => {
      const selectedTemplate = getTemplateById(templateId);
      setValue('type', selectedTemplate?.fCollaborationTemplateTypeFK || '');
    },
    [getTemplateById, setValue],
  );

  const generateTasksFromTemplate = useCallback(
    (
      template: CollaborationWorkListTemplate,
      templateStartDate: Date,
      templateDueDate: Date,
    ): {
      listTasks: WorklistTaskFormData[];
      newTaskInitialData?: WorklistTaskFormData;
    } => {
      const {
        fCollaborationTemplateId,
        fCollaborationTemplateTasks,
        fCollaborationTemplateTypeFK,
      } = template;

      const sortedTasks = sortBy(fCollaborationTemplateTasks, [
        'fCollaborationTemplateTaskSeq',
      ]);
      const listTasks: WorklistTaskFormData[] = sortedTasks.map((task) =>
        convertTemplateTaskToTaskFormData(
          task,
          templateStartDate,
          templateDueDate,
          loggedInUserId,
          selectedEmployeeId,
          employeeData,
          defaultResponsibleHrId || -1,
          fCollaborationTemplateId,
        ),
      );

      const newTaskInitialData =
        listTasks.length > 0
          ? createNewTaskFormData({
              taskType: fCollaborationTemplateTypeFK,
              targetEmployeeId: selectedEmployeeId,
              creatorEmployeeId: loggedInUserId,
            })
          : undefined;

      return {
        listTasks,
        newTaskInitialData,
      };
    },
    [selectedEmployeeId, employeeData, defaultResponsibleHrId, loggedInUserId],
  );

  const updateListTasks = useCallback(
    (
      currentTasks: WorklistTaskFormData[],
      newTasks: WorklistTaskFormData[],
    ): WorklistTaskFormData[] => {
      const getTemplateIdFromTasks = (
        tasks: WorklistTaskFormData[],
      ): number | undefined => {
        return tasks.find((t) => !!t.templateId)?.templateId;
      };

      const currentTemplateId = getTemplateIdFromTasks(currentTasks);
      const newTemplateId = getTemplateIdFromTasks(newTasks);

      // Template not changed, Update only DueDate on template Tasks
      if (currentTemplateId === newTemplateId) {
        return currentTasks.map((ct): WorklistTaskFormData => {
          if (ct.templateId) {
            const matchingTemplateTask = newTasks.find(
              (nt) => nt.taskId === ct.taskId,
            );

            if (!!matchingTemplateTask) {
              return {
                ...ct,
                dueDate: matchingTemplateTask.dueDate ?? ct.dueDate,
              };
            }
          }

          return ct;
        });
      }

      // Reset all Tasks
      return newTasks;
    },
    [],
  );

  const handleResetTemplateTasks = useCallback(
    (templateId: number, templateStartDate: Date, templateDueDate: Date) => {
      const selectedTemplate = getTemplateById(templateId);

      const { listTasks: currentTasks } = getValues();

      if (!!selectedTemplate && templateStartDate && templateDueDate) {
        const { listTasks: newTasks, newTaskInitialData } =
          generateTasksFromTemplate(
            selectedTemplate,
            templateStartDate,
            templateDueDate,
          );

        setValue('listTasks', updateListTasks(currentTasks, newTasks), {
          shouldDirty: true,
          shouldValidate: true,
        });
        setNewTaskData(newTaskInitialData);
      }
    },
    [
      getTemplateById,
      setValue,
      getValues,
      generateTasksFromTemplate,
      updateListTasks,
    ],
  );

  const handleClose = useCallback(
    (resetData?: CollabTemplateFormType) => {
      reset(resetData);
      onDialogClose();
    },
    [reset, onDialogClose],
  );

  const onSave = useMemo(() => {
    return handleSubmit((form) => {
      const payload = convertTaskListToEmployeeTasks(uuidv4(), form);
      setSubmitting(true);

      return createEmployeeTemplateTasks(selectedEmployeeId, payload)
        .then(() => {
          enqueueSnackbar(t('create_employee_template_tasks_success'), {
            variant: 'success',
          });
          onDialogClose();
          onTaskListCreate();
        })
        .catch((err) => {
          enqueueSnackbar(t('error_create_employee_template_tasks'), {
            variant: 'error',
          });
          console.log('err', err);
        })
        .finally(() => {
          setSubmitting(false);
        });
    });
  }, [
    t,
    enqueueSnackbar,
    onTaskListCreate,
    onDialogClose,
    handleSubmit,
    selectedEmployeeId,
  ]);

  const hasInitialChanged = useHasChanged(initialFormData);
  useEffect(() => {
    if (hasInitialChanged) {
      reset(initialFormData);
    }
  }, [hasInitialChanged, reset, initialFormData]);

  return {
    form,
    templateOptions: workListTemplates,
    getTaskTypeName,
    templateDueDate,
    selectedEmployeeId,
    employeeData,
    onSave,
    onClose: () => handleClose(),
    handleResetTemplateTasks,
    handleSyncTemplateType,
    workListTemplateSettings: {
      id: selectedTemplateId,
      dueDate: templateDueDate,
      startDate: templateStartDate,
    },
    isSubmitting: submitting,
    showTasks: !!selectedTemplateId && !!templateDueDate,
    newTaskData,
  };
};
